import { z } from "zod";
import type { Args, Operation } from "../../types";
import { scoped } from "../../types";
import {
  helpArticleRowSchema,
  helpIdParams,
  helpSearchHitSchema,
} from "./_shared";

const list: Operation<
  void,
  { category_slug?: string; include_unpublished?: boolean; limit?: number },
  void,
  { articles: z.infer<typeof helpArticleRowSchema>[] }
> = {
  method: "GET",
  path: () => "/api/help/articles",
  query: z
    .object({
      category_slug: z.string().optional(),
      include_unpublished: z.coerce.boolean().optional(),
      limit: z.coerce.number().int().min(1).max(500).optional(),
    })
    .partial(),
  response: z.object({ articles: z.array(helpArticleRowSchema) }),
};

const get: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof helpArticleRowSchema>
> = {
  method: "GET",
  path: ({ id }) => `/api/help/articles/${id}`,
  params: helpIdParams,
  response: helpArticleRowSchema,
};

const getByPath: Operation<
  void,
  { category_slug: string; slug: string },
  void,
  z.infer<typeof helpArticleRowSchema>
> = {
  method: "GET",
  path: () => "/api/help/articles/by-path",
  query: z.object({
    category_slug: z.string().min(1),
    slug: z.string().min(1),
  }),
  response: helpArticleRowSchema,
};

const create: Operation<
  void,
  void,
  {
    category_slug?: string;
    category_id?: number;
    slug: string;
    title: string;
    summary?: string | null;
    body_md: string;
    sort_key?: string;
    published?: boolean;
  },
  z.infer<typeof helpArticleRowSchema>
> = {
  method: "POST",
  path: () => "/api/help/articles",
  body: z.object({
    category_slug: z.string().optional(),
    category_id: z.number().int().optional(),
    slug: z.string().min(1),
    title: z.string().min(1),
    summary: z.string().nullable().optional(),
    body_md: z.string(),
    sort_key: z.string().optional(),
    published: z.boolean().optional(),
  }),
  response: helpArticleRowSchema,
  // create inserts a row into the collection (and may change category filter sets).
  invalidates: [list],
};

const scopedGet = scoped(
  get,
  (args: Args<{ id: number }, void, unknown>) => ({ id: args.params.id }),
);

const update: Operation<
  { id: number },
  void,
  {
    title?: string;
    summary?: string | null;
    body_md?: string;
    category_slug?: string;
    category_id?: number;
    sort_key?: string;
    published?: boolean;
  },
  z.infer<typeof helpArticleRowSchema>
> = {
  method: "PATCH",
  path: ({ id }) => `/api/help/articles/${id}`,
  params: helpIdParams,
  body: z
    .object({
      title: z.string().min(1).optional(),
      summary: z.string().nullable().optional(),
      body_md: z.string().optional(),
      category_slug: z.string().optional(),
      category_id: z.number().int().optional(),
      sort_key: z.string().optional(),
      published: z.boolean().optional(),
    })
    .partial(),
  response: helpArticleRowSchema,
  // title/summary/published/category all land on list rows + the detail GET.
  invalidates: [list, scopedGet],
};

const archive: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof helpArticleRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/help/articles/${id}/archive`,
  params: helpIdParams,
  response: helpArticleRowSchema,
  // soft-delete removes the row from default list filters; detail gains archived_at.
  invalidates: [list, scopedGet],
};

const search: Operation<
  void,
  { q: string; category_slug?: string; limit?: number },
  void,
  { hits: z.infer<typeof helpSearchHitSchema>[] }
> = {
  method: "GET",
  path: () => "/api/help/articles/search",
  query: z.object({
    q: z.string(),
    category_slug: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
  }),
  response: z.object({ hits: z.array(helpSearchHitSchema) }),
};

const recordView: Operation<
  void,
  void,
  { article_id: number; referrer?: string | null },
  { ok: true }
> = {
  method: "POST",
  path: () => "/api/help/articles/record-view",
  body: z.object({
    article_id: z.number().int(),
    referrer: z.string().nullable().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  // bumps help_articles.view_count — a column on the article row (list + get).
  invalidates: [
    list,
    scoped(
      get,
      (args: Args<void, void, { article_id: number }>) => ({
        id: args.body.article_id,
      }),
    ),
  ],
};

const recordFeedback: Operation<
  void,
  void,
  { article_id: number; value: "helpful" | "unhelpful" },
  { ok: true }
> = {
  method: "POST",
  path: () => "/api/help/articles/record-feedback",
  body: z.object({
    article_id: z.number().int(),
    value: z.enum(["helpful", "unhelpful"]),
  }),
  response: z.object({ ok: z.literal(true) }),
  // bumps helpful_count / unhelpful_count on the article row.
  invalidates: [
    list,
    scoped(
      get,
      (args: Args<void, void, { article_id: number }>) => ({
        id: args.body.article_id,
      }),
    ),
  ],
};

export const articles = {
  list,
  get,
  get_by_path: getByPath,
  create,
  update,
  archive,
  search,
  record_view: recordView,
  record_feedback: recordFeedback,
};
